import {
  CropFieldWithIrrigation,
  DeleteIrrigation, Irrigation,
  RegisterIrrigation,
  SelectedCropField,
} from '@app/irrigations/models/models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { withCustomerParams } from '@app/shared/utils';
import { createRxMutation, createRxResource } from '@next/core-lib/notification';
import { withMutations, withResource } from '@angular-architects/ngrx-toolkit';
import { AppState } from '@app/shared/models';
import { inject } from '@angular/core';
import { ActionsSubject, Store } from '@ngrx/store';
import { UserSelectors } from '@next/core-lib/user';
import { DateService } from '@app/shared/services/date.service';
import { IrrigationService } from '@app/irrigations/services/irrigation.service';
import { cropYearChanged } from '../../store/actions';
import { selectCropYear } from '../../store/selectors';
import { filter, map } from 'rxjs';


interface IrrigationsState {
  selectedCropFields: SelectedCropField[];
  modifiedCropFieldIds: number[];
}

const initialState: IrrigationsState = {
  selectedCropFields: [],
  modifiedCropFieldIds: [],
};

interface IrrigationsResourceParams {
  customerId: number;
  cropFieldIds: number[];
  cropYear: number;
}

const sortCropFieldsByNameAsc = (a: CropFieldWithIrrigation, b: CropFieldWithIrrigation) => a.name.localeCompare(b.name)

const sortIrrigationByDateDesc = (a: Irrigation, b: Irrigation) => new Date(b.startedOn).getTime() - new Date(a.startedOn).getTime();

export const IrrigationsStore = signalStore(
  withState(initialState),
  withComputed(() => ({
    cropYear: inject(Store<AppState>).selectSignal(selectCropYear),
  })),
  withResource((store) => {
    const appStore = inject(Store<AppState>);
    const irrigationService = inject(IrrigationService);

    const customer = appStore.selectSignal(UserSelectors.selectCustomer);

    const irrigations = createRxResource<CropFieldWithIrrigation[], IrrigationsResourceParams | undefined>({
      params: () =>
        withCustomerParams(customer()?.id, (customerId) => {
          return { cropFieldIds: store.selectedCropFields().map((c) => c.id), cropYear: store.cropYear(), customerId };
        }),
      stream: ({ params }) =>
        irrigationService.getIrrigations(params.cropFieldIds, params.cropYear),
      defaultValue: [],
      errorMessage: 'errors.irrigation.general',
    });

    return {
      irrigations,
    }
  }),
  withComputed((store) => {
    return {
      sortedSelectedCropFields(): SelectedCropField[] {
        return store.selectedCropFields().sort((a, b) => a.name.localeCompare(b.name));
      },
      sortedCropFieldsWithIrrigations(): CropFieldWithIrrigation[] {
        const cropFields = store.irrigationsValue() ?? [];
        return cropFields
          .map((field) => ({
            ...field,
            irrigations: [...field.irrigations].sort((a, b) => sortIrrigationByDateDesc(a, b)),
          }))
          .sort((a, b) => sortCropFieldsByNameAsc(a, b));
      },
      selectedCropFieldIds(): number[] {
        return store.selectedCropFields().map((field) => field.id);
      },
    };
  }),
  withMethods((store) => ({
    selectCropFields(cropFields: SelectedCropField[]): void {
      patchState(store, { selectedCropFields: cropFields });
    },
    deselectCropField(cropFieldId: number): void {
      const selectedCropFields = store.selectedCropFields().filter((field) => field.id !== cropFieldId);
      patchState(store, { selectedCropFields });
    },
    updateCropFields(cropFields: CropFieldWithIrrigation[]): void {
      patchState(store, { irrigationsValue: cropFields });
    },
    deleteIrrigationFromStore(cropFieldId: number, irrigationId: number): void {
      const cropFields = store.irrigationsValue().map((field) => {
        if (field.id === cropFieldId) {
          return {
            ...field,
            irrigations: field.irrigations.filter((irrigation) => irrigation.id !== irrigationId),
          }
        }

        return field;
      });
      patchState(store, { irrigationsValue: cropFields });
    },
  })),
  withMutations((store) => {
    const irrigationService = inject(IrrigationService);
    const dateService = inject(DateService);
    const trackModifiedCropFieldIds = (cropFieldIds: number[]): void => {
      patchState(store, {
        modifiedCropFieldIds: Array.from(new Set([...store.modifiedCropFieldIds(), ...cropFieldIds])),
      });
    };

    return {
      registerIrrigation: createRxMutation<RegisterIrrigation, CropFieldWithIrrigation[]>({
        operation: (request) => irrigationService.registerIrrigation(request),
        onSuccess: (result, request) => {
          store.updateCropFields(result);
          trackModifiedCropFieldIds(request.cropFieldIds);
        },
        notification: {
          successMessage: 'success.irrigation.registered',
          errorMessage: 'errors.irrigation.register',
        },
      }),
      deleteIrrigation: createRxMutation<DeleteIrrigation, void>({
        operation: (request) =>
          irrigationService.deleteIrrigation(request.cropFieldId, request.irrigationId, dateService.currentYear()),
        onSuccess: (_, result) => {
          store.deleteIrrigationFromStore(result.cropFieldId, result.irrigationId);
          trackModifiedCropFieldIds([result.cropFieldId]);
        },
        notification: {
          successMessage: 'success.irrigation.deleted',
          errorMessage: 'errors.irrigation.delete',
        },
      }),
    }
  }),
  withHooks({
    onInit(store): void {
      inject(ActionsSubject)
        .pipe(
          filter((action) => action.type === cropYearChanged.type),
          map((action) => action as ReturnType<typeof cropYearChanged>),
          takeUntilDestroyed(),
        )
        .subscribe(() => {
          patchState(store, {
            selectedCropFields: [],
            modifiedCropFieldIds: [],
            irrigationsValue: [],
          });
        });
    },
  }),
);
